import { gunzipSync, gzipSync } from 'fflate'
import { IngestEnvelopeSchema, type IngestEnvelope } from '@collector/shared'
import { decodeCborEnvelope, type CborEnvelope } from './tcccCbor'

/**
 * Encode an envelope as a compact, QR-friendly string. We gzip the JSON, then
 * base64url so it survives the QR alphanumeric mode boundary. The decoder
 * accepts:
 *   - `c1.` prefix → our legacy gzip-JSON format,
 *   - leading `{`  → raw JSON (debug / hand-crafted),
 *   - otherwise    → CBOR wire format from the Android tablet (base64 of
 *                    optionally-gzipped CBOR; see `tcccCbor.ts`).
 */
const COMPRESSED_PREFIX = 'c1.'
const GZIP_MAGIC_0 = 0x1f
const GZIP_MAGIC_1 = 0x8b

export function encodeEnvelopeForQr(envelope: IngestEnvelope): string {
  const pruned = pruneEmptyForQr(envelope)
  const json = JSON.stringify(pruned)
  const compressed = gzipSync(new TextEncoder().encode(json))
  return COMPRESSED_PREFIX + base64UrlEncode(compressed)
}

/**
 * Strip keys whose value is an empty string, empty array, or empty object
 * from the envelope's `patient` and `data` before encoding. Keeps the
 * envelope's required top-level keys (schema, tabletUid, capturedAt,
 * patient, data) even if a subtree collapses to `{}`, so the decoder's
 * Zod validation still accepts the result.
 *
 * This is a QR-size optimisation only — the trip through `/decode-label` lands
 * back in the same renderer we use for live data sets, which already
 * tolerates missing optional fields.
 */
export function pruneEmptyForQr(envelope: IngestEnvelope): IngestEnvelope {
  const prunedPatient = pruneEmptyDeep(envelope.patient) as IngestEnvelope['patient']
  const prunedData = (pruneEmptyDeep(envelope.data) ?? {}) as Record<string, unknown>
  return {
    ...envelope,
    patient: prunedPatient,
    data: prunedData,
  }
}

function pruneEmptyDeep(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(pruneEmptyDeep).filter((v) => !isEmpty(v))
  }
  if (value !== null && typeof value === 'object') {
    const out: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(value)) {
      const pruned = pruneEmptyDeep(v)
      if (!isEmpty(pruned)) out[k] = pruned
    }
    return out
  }
  return value
}

function isEmpty(value: unknown): boolean {
  if (value === '' || value === null || value === undefined) return true
  if (Array.isArray(value)) return value.length === 0
  if (typeof value === 'object') return Object.keys(value as Record<string, unknown>).length === 0
  // `0` and `false` are meaningful clinical values (e.g., pain=0) and must survive.
  return false
}

export function decodeEnvelopeFromQr(payload: string): IngestEnvelope {
  const trimmed = payload.trim()
  if (trimmed.startsWith(COMPRESSED_PREFIX)) {
    const bytes = base64UrlDecode(trimmed.slice(COMPRESSED_PREFIX.length))
    const json = new TextDecoder().decode(gunzipSync(bytes))
    return IngestEnvelopeSchema.parse(JSON.parse(json))
  }
  if (trimmed.startsWith('{')) {
    return IngestEnvelopeSchema.parse(JSON.parse(trimmed))
  }
  // Fall through to CBOR. The Android tablet emits base64(NO_WRAP) of
  // gzip(cbor(...)); some emitters skip gzip for tiny payloads, so we sniff
  // the gzip magic and pass the bytes through as-is otherwise.
  const raw = base64Decode(trimmed)
  const cbor = isGzipped(raw) ? gunzipSync(raw) : raw
  const env = decodeCborEnvelope(cbor)
  return IngestEnvelopeSchema.parse(envelopeFromCbor(env))
}

/**
 * Synthesize the ingest-envelope wrapper around a CBOR-decoded body. The
 * tablet-side CBOR carries the card payload only, so `tabletUid` and the
 * `patient` header are derived (or stubbed) here. Field-scanned cards
 * usually flow through `/decode-label` which never persists, so the synthetic
 * `tabletUid` is operationally inert — it just satisfies validation.
 */
function envelopeFromCbor(env: CborEnvelope): unknown {
  const body = env.body
  const demographics = (body.demographics ?? {}) as Record<string, unknown>
  const lastName = typeof demographics.lastName === 'string' ? demographics.lastName : ''
  const capturedAt =
    typeof body.capturedAt === 'string' ? body.capturedAt : new Date().toISOString()
  return {
    schema: `${env.schemaName}@${env.version}`,
    tabletUid: 'qr-scanned',
    capturedAt,
    patient: { name: lastName.length > 0 ? lastName : 'Unknown' },
    data: body,
  }
}

function isGzipped(bytes: Uint8Array): boolean {
  return bytes.length >= 2 && bytes[0] === GZIP_MAGIC_0 && bytes[1] === GZIP_MAGIC_1
}

function base64Decode(s: string): Uint8Array {
  // Zebra scanners in HID-POS mode prepend a three-character AIM code ID
  // (`]` + designator + modifier; `]Q1` for QR) to identify the symbology
  // that was read. Those letters fall inside the base64 alphabet so we
  // can't simply filter on alphabet — strip the whole 3-char prefix
  // explicitly, then drop any remaining non-alphabet chars (e.g. trailing
  // CR/LF configured as a scan suffix).
  let body = s
  if (body.startsWith(']') && body.length >= 3) body = body.slice(3)
  const cleaned = body.replace(/[^A-Za-z0-9+/=_-]/g, '')
  const normalized = cleaned.replaceAll('-', '+').replaceAll('_', '/').replace(/=+$/, '')
  const padLen = (4 - (normalized.length % 4)) % 4
  const bin = atob(normalized + '='.repeat(padLen))
  const out = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i)
  return out
}

function base64UrlEncode(bytes: Uint8Array): string {
  let bin = ''
  for (const byte of bytes) bin += String.fromCharCode(byte)
  return btoa(bin).replaceAll('+', '-').replaceAll('/', '_').replace(/=+$/, '')
}

function base64UrlDecode(s: string): Uint8Array {
  const padded = s.replaceAll('-', '+').replaceAll('_', '/')
  const padLen = (4 - (padded.length % 4)) % 4
  const bin = atob(padded + '='.repeat(padLen))
  const out = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i)
  return out
}
